import React, { useState, useEffect, useMemo } from 'react';
import { Tabs, Spin, Alert, Typography } from 'antd';
import { BankOutlined } from '@ant-design/icons';
import { useFinanceApi } from './api/financeApi';
import {
  OdemelerTab, TahsilatlarTab, DuzenliOdemelerTab, OdemeTalebiTab,
  CariTab, IslemTuruTab, FirmaTab, BankaHesapTab,
} from './tabs/genericTabs';
import KredilerTab from './tabs/KredilerTab';
import CeklerTab from './tabs/CeklerTab';
import FaturaEslestirmeTab from './tabs/FaturaEslestirmeTab';
import HakedisTab from './tabs/HakedisTab';
import SatinAlmaBulkTab from './tabs/SatinAlmaBulkTab';
import YetkilerTab from './tabs/YetkilerTab';
import './Finans.css';
import '../ortak/nfmodules.css';

const { Title } = Typography;

const TAB_DEFS = [
  { module: 'odemeler', label: 'Ödemeler', Comp: OdemelerTab },
  { module: 'tahsilatlar', label: 'Tahsilatlar', Comp: TahsilatlarTab },
  { module: 'duzenli_odemeler', label: 'Düzenli Ödemeler', Comp: DuzenliOdemelerTab },
  { module: 'odeme_talebi', label: 'Ödeme Talebi', Comp: OdemeTalebiTab },
  { module: 'cari', label: 'Cari', Comp: CariTab },
  { module: 'islem_turu', label: 'İşlem Türleri', Comp: IslemTuruTab },
  { module: 'firma', label: 'Firmalar', Comp: FirmaTab },
  { module: 'banka_hesap', label: 'Banka Hesapları', Comp: BankaHesapTab },
  { module: 'fatura_eslestirme', label: 'Fatura Eşleştirme', Comp: FaturaEslestirmeTab },
  { module: 'krediler', label: 'Krediler', Comp: KredilerTab },
  { module: 'cekler', label: 'Çekler', Comp: CeklerTab },
  { module: 'hakedis', label: 'Hakediş', Comp: HakedisTab },
  { module: 'satinalma', label: 'Satın Alma', Comp: SatinAlmaBulkTab },
  { module: 'yetkiler', label: 'Yetkiler', Comp: YetkilerTab },
];

export default function FinansPage() {
  const api = useFinanceApi();
  const [lookups, setLookups] = useState(null);
  const [permData, setPermData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [lk, pm] = await Promise.all([api.lookups(), api.myPermissions()]);
        if (!active) return;
        setLookups(lk);
        setPermData(pm);
      } catch (e) {
        if (active) setError(e?.response?.data?.detail || 'Finans verileri yüklenemedi');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  // module -> {view,create,edit,delete} ; unspecified => allowed (legacy default açık)
  const perms = useMemo(() => {
    const out = {};
    const defaultAllow = !permData || permData.default_allow;
    const stored = (permData && permData.permissions) || {};
    TAB_DEFS.forEach(({ module }) => {
      const m = stored[module];
      const v = (act) => (defaultAllow || !m || m[act] === undefined ? true : !!m[act]);
      out[module] = { view: v('view'), create: v('create'), edit: v('edit'), delete: v('delete') };
    });
    return out;
  }, [permData]);

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><Spin size="large" /></div>;
  }
  if (error) {
    return <div style={{ padding: 24 }}><Alert type="error" showIcon message="Hata" description={error} /></div>;
  }

  const items = TAB_DEFS
    .filter(({ module }) => perms[module].view)
    .map(({ module, label, Comp }) => ({
      key: module,
      label,
      children: <Comp lookups={lookups} perms={perms} />,
    }));

  return (
    <div className="finans-page nf-module">
      <Title level={3} style={{ marginTop: 0 }}>
        <BankOutlined style={{ marginRight: 8 }} /> Nakit Akış
      </Title>
      {items.length === 0 ? (
        <Alert type="warning" showIcon message="Erişim yok"
          description="Bu sayfadaki hiçbir sekme için görüntüleme yetkiniz bulunmuyor. Lütfen yöneticinizle iletişime geçin." />
      ) : (
        <Tabs items={items} type="card" destroyInactiveTabPane />
      )}
    </div>
  );
}
